/**
 * Utility functions for reading and writing URL query strings
 */

/**
 * Parse query string parameters from the current URL
 */
export function parseQueryString(): Record<string, string> {
    if (typeof window === 'undefined') {
        return {}
    }

    const params = new URLSearchParams(window.location.search)
    const result: Record<string, string> = {}

    for (const [key, value] of params.entries()) {
        result[key] = value
    }

    return result
}

/**
 * Set query string parameters in the current URL without causing a page reload
 */
export function setQueryString(
    params: Record<string, string | undefined>,
): void {
    if (typeof window === 'undefined') {
        return
    }

    const url = new URL(window.location.href)

    // Clear existing search params
    url.search = ''

    // Add new params
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null && value !== '') {
            url.searchParams.set(key, value)
        }
    }

    // Update URL without reload
    window.history.replaceState({}, '', url.toString())
}

/**
 * Remove specific query string parameters from the current URL
 */
export function removeQueryStringParams(keys: string[]): void {
    if (typeof window === 'undefined') {
        return
    }

    const url = new URL(window.location.href)

    for (const key of keys) {
        url.searchParams.delete(key)
    }

    window.history.replaceState({}, '', url.toString())
}

/**
 * Get a specific query string parameter value
 */
export function getQueryStringParam(key: string): string | null {
    if (typeof window === 'undefined') {
        return null
    }

    const params = new URLSearchParams(window.location.search)
    return params.get(key)
}

/**
 * Check if a query string parameter exists
 */
export function hasQueryStringParam(key: string): boolean {
    if (typeof window === 'undefined') {
        return false
    }

    const params = new URLSearchParams(window.location.search)
    return params.has(key)
}
